import CheckIcon from '@mui/icons-material/Check'
import { Box } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import eyedrop from '@/assets/images/enamelEyeDrop.svg'
import Button from '@/components/Button'
import Card from '@/components/Card'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import Select from '@/components/Select'
import SnackbarTooltip from '@/components/SnackbarTooltip'
import type { Metadata } from '@/pages/stainingStudio/crown/StaingingStudio'
import { useAppSelector } from '@/store'
import { useGetMetadataQuery } from '@/store/apiService'
import {
  setInternalEffectCeramicBrand,
  setSelectedInternalEffect,
} from '@/store/pfmCrown'
import { INTERNAL_EFFECTS_BACKGROUND } from '@/utils/constants'
import { getOptions, sortAlphabetically } from '@/utils/helper'

type Effect = {
  label: string
  value: string
}
function InternalEffectPanel({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement>
}) {
  const { t } = useTranslation('translation', { keyPrefix: 'internalEffects' })
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const dispatch = useDispatch()
  const { data: metadata } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as {
    data: Metadata
    error: any
    isLoading: boolean
  }

  const options = useMemo(
    () => ({
      ceramicBrands: sortAlphabetically(
        getOptions(metadata, 'available_ceramic_brands'),
        'text',
      ),
      internalEffects: getOptions(metadata, 'available_effecttypes'),
    }),
    [metadata],
  )

  const internalEffectCeramicBrand = useAppSelector(
    (state) => state.pfmCrown.internalEffectCeramicBrand,
  )

  const selectedInternalEffect = useAppSelector(
    (state) => state.pfmCrown.selectedInternalEffect,
  )

  const { control } = useForm({
    defaultValues: {
      ceramicBrands: internalEffectCeramicBrand,
    },
    mode: 'onChange',
  })

  useEffect(() => {
    if (!selectedInternalEffect) {
      setSnackbarOpen(false)
    }
  }, [selectedInternalEffect])

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      gap={1}
      overflow="auto"
    >
      <Card>
        <Select
          options={options.ceramicBrands}
          title={t('ceramicBrand')}
          tooltipText={t('ceramicBrandTooltip')}
          control={control}
          name="ceramicBrands"
          onFormValueChange={(name, value) => {
            dispatch(setInternalEffectCeramicBrand(value))
          }}
          titleSx={{ fontSize: '18px', fontWeight: 400 }}
          placeholder="Select a Brand"
          optionDisabledChecker={(item) => {
            return !item.internal_effects_activated
          }}
        />
      </Card>

      <Card>
        <Box display="flex" flexDirection="column" gap={3}>
          <LabelWithTooltip
            label={t('matisseInternalEffects')}
            tooltipText={t('matisseInternalEffectsTooltip')}
            titleSx={{ fontSize: '18px', fontWeight: 400 }}
          />
          {options.internalEffects?.map((effect: Effect) => (
            <Box key={effect.value} display="flex" alignItems="center" gap={3}>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  fontSize: '16px',
                  padding: '8px 10px',
                  cursor: 'pointer',
                  lineHeight: 'normal',
                  height: '36px',
                  width: '36px',
                  minWidth: '36px',
                  borderRadius: '4px',
                }}
                textTransform="uppercase"
                onClick={() => {
                  dispatch(
                    setSelectedInternalEffect(
                      selectedInternalEffect === effect.value
                        ? ''
                        : effect.value,
                    ),
                  )
                  setSnackbarOpen(true)
                }}
                label={
                  selectedInternalEffect === effect.value ? (
                    <CheckIcon fontSize="small" />
                  ) : (
                    <img src={eyedrop} alt="Select" width={16} height={16} />
                  )
                }
              />
              <Button
                label={effect.value}
                sx={{
                  color: 'black',
                  background: INTERNAL_EFFECTS_BACKGROUND[effect.value],
                  fontSize: '16px',
                  padding: '8px 10px',
                  flex: 1,
                  pointerEvents: 'none',
                  lineHeight: 'normal',
                }}
              />
            </Box>
          ))}
        </Box>
      </Card>
      <SnackbarTooltip
        containerRef={containerRef}
        message="Click on the spot you want to measure."
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        placement="top"
        offset={[0, -100]}
        maxWidth={500}
        autoHideDuration={6000}
      />
    </Box>
  )
}

export default InternalEffectPanel
